<template>
  <ModalAlertaOK ref="modalSessaoExpirada" titulo="Atenção"
    :mensagem="`Sessão expirada por inatividade. Você será redirecionado para o login!`"
    @onSim="confirmarEncerramentoSessao()" />

  <LoaderFullScreen v-if="loading" />

  <!-- O diálogo do sistema (confirmar/avisar/perguntar) — o substituto de window.confirm/alert/prompt,
       que o sistema NUNCA usa. Ver services/globals/dialogo.service.ts. -->
  <DialogoSistema />

  <!--
    Região viva única da aplicação, alimentada pelo `router.afterEach`
    (services/globals/anuncio-navegacao.service.ts). Fica FORA do <component :is="layout">
    de propósito: se estivesse dentro, o próprio elemento seria recriado a cada troca de
    layout e o leitor de tela perderia a região que estava observando — o anúncio
    simplesmente não sairia.

    Nunca visível e sem ocupar espaço (`.regiao-anuncio`), mas presente no DOM desde o
    início, que é o que permite ao leitor de tela observar as mudanças de conteúdo.
  -->
  <p id="anuncio-navegacao" class="regiao-anuncio" role="status" aria-live="polite" aria-atomic="true"></p>

  <component :is="layout">
    <router-view v-model:layout.sync="layout" :key="keyRV" />
  </component>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from "vue";
import ModalAlertaOK from "@/components/modal/ModalAlertaOK.vue";
import DialogoSistema from "@/components/modal/DialogoSistema.vue";
import SessionTimeoutService from '@/services/globals/session-timeout.service';
import { mapActions, mapGetters } from "vuex";
import { useStore } from "@/store";
import { useRouter } from 'vue-router';
import { MenuActionTypes } from "./store/menu/action-types";
import sessionService from "./services/helper/session.service";
import toastService from "./services/globals/toast.service";
import configuracaoSegurancaService, { versaoConfiguracaoSeguranca } from "@/services/ferramentas/saeweb/configuracaoSeguranca.service";
import DadosLocais, { versaoCodContrato } from "@/services/helper/storage";

const dadosLocais = DadosLocais.getInstance();
const MINUTOS_INATIVIDADE_PADRAO = 120;
const MINUTOS_AVISO_PADRAO = 1;

export default defineComponent({
  name: "App",
  components: { DialogoSistema, ModalAlertaOK },
  setup() {
    const modalSessaoExpirada = ref();
    const router = useRouter();
    const minutosAvisoAntes = ref(MINUTOS_AVISO_PADRAO);
    const sessionTimeoutAviso = new SessionTimeoutService((MINUTOS_INATIVIDADE_PADRAO - MINUTOS_AVISO_PADRAO) * 60 * 1000);
    const sessionTimeoutEncerramento = new SessionTimeoutService(MINUTOS_INATIVIDADE_PADRAO * 60 * 1000);
    let avisoSessaoAtivo = false;

    const handleUserActivity = () => {
      // Os listeners de mousemove/keydown ficam ativos mesmo fora de uma sessão (ex: usuário
      // digitando login/senha na tela de login) — sem sessão, não há o que renovar ou avisar.
      if (!sessionService.TemSessaoAtiva()) {
        return;
      }

      if (avisoSessaoAtivo) {
        avisoSessaoAtivo = false;
        toastService.fechar();
        toastService.alertaSucesso("Sessão renovada com sucesso.");
      }

      sessionTimeoutAviso.reset(() => {
        avisoSessaoAtivo = true;
        toastService.alertaAviso(
          `Por favor, continue utilizando o sistema para manter sua sessão ativa. Caso contrário, ela será encerrada em ${minutosAvisoAntes.value} minuto(s).`,
          minutosAvisoAntes.value * 60 * 1000,
          true,
          "Renovar Minha Sessão",
        );
      });

      sessionTimeoutEncerramento.reset(() => {
        endSession();
      });
    };

    const endSession = () => {
      avisoSessaoAtivo = false;

      /*
       * Fecha o aviso "sua sessão será encerrada em X minuto(s)" ANTES de abrir o modal de
       * sessão expirada.
       *
       * O aviso deixou de ter temporizador (ver o comentário de 2.2.1 em
       * toast.service.ts: aviso com botão de ação não pode fechar sozinho, senão o botão
       * fica inalcançável por teclado). Sem fechá-lo aqui, ele permaneceria na tela ao
       * lado do modal, com duas mensagens se contradizendo — uma pedindo para continuar
       * usando o sistema e outra dizendo que a sessão já acabou. Mensagem contraditória é
       * barreira de compreensão (WCAG 3.3.1 / 3.2.4).
       */
      toastService.fechar();

      if (sessionService.TemSessaoAtiva()) {
        sessionService.RealizarLogoff('INATIVIDADE');
        modalSessaoExpirada.value?.openModal();
      }
    };

    const confirmarEncerramentoSessao = () => {
      router.replace("/login");
    };

    const aplicarConfiguracaoTimeoutSessao = async () => {
      const codigoContratoAtual = Number(dadosLocais.getCodContrato());
      if (!codigoContratoAtual) return;

      try {
        const { data } = await configuracaoSegurancaService.obterConfiguracao(codigoContratoAtual);

        minutosAvisoAntes.value = data.timeoutAvisoAntesMinutos;
        const minutosAtePrimeiroAviso = Math.max(
          0,
          data.timeoutInatividadeMinutos - data.timeoutAvisoAntesMinutos,
        );

        sessionTimeoutAviso.setTimeoutDuration(minutosAtePrimeiroAviso * 60 * 1000);
        sessionTimeoutEncerramento.setTimeoutDuration(data.timeoutInatividadeMinutos * 60 * 1000);
        sessionTimeoutEncerramento.start(endSession);
      } catch (error) {
        // Se a configuração customizada não puder ser buscada, os timers já iniciados com os
        // valores padrão (MINUTOS_INATIVIDADE_PADRAO/MINUTOS_AVISO_PADRAO) continuam valendo —
        // registra para não mascarar silenciosamente um timeout configurado mais curto que nunca
        // chegou a ser aplicado.
        console.error('Falha ao obter configuração de timeout de inatividade do contrato; mantendo os valores padrão.', error);
      }
    };

    onMounted(async () => {
      sessionTimeoutEncerramento.start(endSession);
      window.addEventListener('mousemove', handleUserActivity);
      window.addEventListener('keydown', handleUserActivity);

      if (sessionService.TemSessaoAtiva()) {
        await aplicarConfiguracaoTimeoutSessao();
      }
    });

    // App.vue monta uma única vez, na raiz da SPA — se o carregamento inicial cair na tela de
    // login, ainda não há contrato nenhum e a busca acima (dentro do onMounted) é pulada. Este
    // watch cobre o caso comum: login sem reload de página, direto na mesma sessão do navegador.
    // Reage também ao salvar um novo valor na tela de Configurações de Segurança — sem isto, quem
    // já está logado só veria o valor novo depois de relogar/recarregar.
    watch([versaoCodContrato, versaoConfiguracaoSeguranca], () => {
      if (sessionService.TemSessaoAtiva()) {
        aplicarConfiguracaoTimeoutSessao();
      } else {
        // Logoff: cancela qualquer aviso/encerramento já agendado de antes — sem isto, um timer
        // pendente da sessão anterior ainda dispararia o toast de aviso na tela de login.
        sessionTimeoutAviso.clear();
        sessionTimeoutEncerramento.clear();
        avisoSessaoAtivo = false;
      }
    });

    onBeforeUnmount(() => {
      sessionTimeoutAviso.clear();
      sessionTimeoutEncerramento.clear();
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    });

    return {
      modalSessaoExpirada,
      confirmarEncerramentoSessao
    }
  },
  watch: {
    EscolaSelecionada() {
      this.reload();
    },
    AnoSelecionado() {
      this.reload();
    },
    SemestreSelecionado() {
      this.reload();
    },
    GrupoUsuarioSelecionado() {
      this.reload();
    },
  },
  data() {
    return {
      keyRV: 0 as number,
      layout: 'div'
    };
  },
  computed: {
    ...mapGetters({
      GrupoUsuarioSelecionado: "getCodigoGrupoUsuario",
      EscolaSelecionada: "getCodigoEscola",
      AnoSelecionado: "getAno",
      SemestreSelecionado: "getSemestre",
    }),
    loading() {
      return useStore().state.loader.loading;
    }
  },
  methods: {
    ...mapActions([MenuActionTypes.ACTION_SET_CODIGOGRUPOUSUARIO]),
    reload() {
      const meta = this.$route.meta as any;

      if (meta?.routeOnChangeEscolaAnoSemestre) {
        this.$router.replace({
          name: meta.routeOnChangeEscolaAnoSemestre
        });
        return;
      }

      if (this.$route.name !== 'proxyLegado') {
        this.keyRV += 1;
      }
    }
  }

});
</script>

<style lang="scss">
@import '@/assets/scss/app-modern.scss';
@import '@/assets/scss/icons.scss';
/* Foco visível, alvo de toque, movimento reduzido e convivência com o widget
   Sienna. Vem DEPOIS do tema de propósito: várias regras de lá precisam ser
   vencidas (o tema zera o outline em 30+ seletores). */
@import '@/assets/scss/acessibilidade';
/* Acessibilidade dos componentes compartilhados (combos, abas, tabelas, tooltips,
   paginação). Ponto único: antes essas regras viviam em blocos <style> NÃO scoped de
   componentes de tela, e a ordem de injeção decidia quem vencia. */
@import '@/assets/scss/acessibilidade-componentes';

/* ==========================================================================
   Cor de destaque do sistema

   A mesma da barra superior (.navbar-custom.topnav-navbar) e dos acordeões.
   Fica aqui, numa variável, para não haver duas versões do roxo espalhadas
   pelas telas — quem precisar da cor usa var(--saeweb-destaque).
   ========================================================================== */
:root {
  --saeweb-destaque: #5a0b83;
  --saeweb-destaque-hover: #4a0a6d;
  --saeweb-destaque-suave: rgba(90, 11, 131, 0.12);
  --saeweb-destaque-texto: #ffffff;
}

html[data-theme='dark'] {
  --saeweb-destaque: #222023;
  --saeweb-destaque-hover: #313033;
  --saeweb-destaque-suave: rgba(255, 255, 255, 0.08);
  --saeweb-destaque-texto: #ffffff;
}

/* ==========================================================================
   Abas (nav-tabs) — padrão único do sistema

   Antes cada tela pintava as próprias abas, e em várias delas não dava para
   saber qual estava selecionada. Agora a aba ativa usa a cor de destaque, com
   texto branco; as demais ficam na cor de destaque sobre fundo transparente.
   Vale para toda .nav-tabs, então tela nova já nasce padronizada.
   ========================================================================== */
.nav-tabs {
  border-bottom: 2px solid var(--saeweb-destaque);
  gap: 2px;
  flex-wrap: wrap;

  /* O !important é necessário: o tema Hyper define .nav-tabs .nav-link com cor de texto
     apagada (cinza) em regras mais específicas, e a aba inativa ficava ilegível. */
  .nav-link {
    border: 1px solid transparent;
    border-radius: 6px 6px 0 0;
    color: var(--saeweb-destaque) !important;
    font-weight: 600;
    padding: 8px 14px;
    transition: all 0.2s ease;

    &:hover:not(.active) {
      background: var(--saeweb-destaque-suave);
      border-color: transparent;
      color: var(--saeweb-destaque) !important;
    }

    &.active {
      background: var(--saeweb-destaque) !important;
      border-color: var(--saeweb-destaque) !important;
      color: var(--saeweb-destaque-texto) !important;
      box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
    }

    &.disabled {
      color: var(--ct-text-muted, #98a6ad) !important;
    }
  }
}

/* No tema escuro a barra é quase preta; a aba inativa precisa de um roxo claro para continuar
   legível sobre o fundo escuro. */
html[data-theme='dark'] .nav-tabs .nav-link:not(.active) {
  color: #b98cd8 !important;
}

/* Acordeões usam a mesma cor, para a tela inteira falar a mesma língua. */
.accordion-button.bg-primary,
.accordion-button:not(.collapsed) {
  background-color: var(--saeweb-destaque) !important;
  color: var(--saeweb-destaque-texto) !important;
}

.navbar-custom.topnav-navbar {
  background-color: var(--saeweb-destaque) !important;
}

body.dashboard-fullscreen .footer,
body.dashboard-fullscreen footer,
body.dashboard-fullscreen .content-page>.footer {
  display: none !important;
}

body.dashboard-fullscreen .content-page,
body.dashboard-fullscreen .content,
body.dashboard-fullscreen .container-fluid {
  margin: 0 !important;
  padding: 0 !important;
  max-width: 100% !important;
}

html[data-theme="dark"] {

  .content,
  .page-wrapper,
  main,
  .container {
    background-color: var(--ct-body-bg) !important;
    color: var(--ct-body-color) !important;
  }

  .navbar-custom.topnav-navbar,
  .simplebar-wrapper,
  .div-multiselect-modulo {
    background-color: #222023 !important;
  }

  .multiselect-dropdown {
    scrollbar-color: rgba(255, 255, 255, 0.32) transparent;

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.32);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  #app {
    background-color: #222023 !important;
  }

  .news-container {
    background-color: #262626 !important;
  }

  .text-primary,
  .text-muted {
    color: #cecece !important;
  }
}


html[data-theme='dark'] {
  .swal2-toast {
    background-color: var(--ct-bg-topbar);
    color: white;
  }

  .swal2-timer-progress-bar {
    background-color: white;
  }
}



/*
 * Mensagem de erro do vee-validate (<ErrorMessage> renderiza <span role="alert">).
 *
 * A COR saiu daqui: era #dc3545 = 4,53:1 sobre branco, que reprova em 1.4.6 (AAA exige
 * 7:1) — e esta é a mensagem de erro de todo formulário validado do sistema. Ela agora
 * vem de `_acessibilidade-componentes.scss`, junto das outras tintas de estado, onde o
 * número de contraste fica registrado ao lado do valor.
 *
 * O que fica aqui é só a MÉTRICA do bloco (ocupar a linha inteira, abaixo do campo).
 */
span[role=alert] {
  width: 100%;
  margin-top: .25rem;
  font-size: .875em;
}

.card form label+.form-control {
  margin-bottom: unset !important;
}

.swal2-actions .btn {
  margin: 0px 8px 0px 8px;
}

.multiselect.is-active {
  box-shadow: none !important;
}

/* ────────────────────────────────────────────────────────────────────────────
   @vueform/multiselect — ajustes globais de usabilidade (desktop e mobile).

   Este bloco de estilo do App.vue NÃO é scoped, então os seletores já valem para
   toda a aplicação — por isso não se usa :deep() aqui (em bloco não-scoped o
   :deep() não é compilado e o navegador descarta a regra inteira).

   Regra de rolagem: o dropdown cresce conforme a quantidade de opções até um teto
   relativo à altura da tela (--ms-max-height). Poucos itens => sem barra de
   rolagem; muitos itens => trava no teto e rola. Quem produz esse comportamento é
   o par max-height + overflow-y:auto — o tema vem com overflow-y:scroll, que
   mostra a barra sempre, mesmo com 2 opções.

   Preferimos as variáveis do próprio tema (--ms-*) a sobrescritas com !important:
   o tema já as lê via var(), então basta declarar o valor.

   ATENÇÃO ao mexer em combo: NÃO recrie `max-height: fit-content !important` em
   :deep(.multiselect-dropdown)/(.multiselect-options). Vinte e dois componentes
   traziam esse par copiado entre si, e ele anulava o teto daqui — o dropdown
   crescia até caber TODAS as opções, passava do rodapé da tela e nunca ganhava
   barra de rolagem. Se um combo específico precisar de outro teto, declare
   `--ms-max-height` no escopo dele em vez de soltar o max-height. ───────────── */
:root {
  /* Teto no desktop: o menor entre ~11 opções e metade da tela. */
  --ms-max-height: min(22rem, 50vh);
  /* Respiro das opções (10px 12px). */
  --ms-option-py: 0.625rem;
  --ms-option-px: 0.75rem;
}

/* Celular em pé: a tela é alta, vale usar mais espaço. dvh desconta a barra de
   endereço do navegador móvel, que vh ignora. */
@media (max-width: 575.98px) {
  :root {
    --ms-max-height: 60vh;
  }

  @supports (height: 1dvh) {
    :root {
      --ms-max-height: 60dvh;
    }
  }
}

/* Telas baixas (celular deitado, notebook pequeno): não tomar a tela inteira. */
@media (max-height: 520px) {
  :root {
    --ms-max-height: 65vh;
  }
}

.multiselect-dropdown {
  /* auto = barra só quando o conteúdo passa do teto (o tema usa scroll). */
  overflow-y: auto !important;
  /* Nunca rola na HORIZONTAL: rótulo largo quebra em mais linhas, não empurra a lista
     para o lado. Sem isto o texto some na borda direita e só reaparece arrastando —
     e como `overflow-y` não é `visible`, o `overflow-x` computa para `auto` sozinho,
     ou seja, a rolagem lateral aparecia sem ninguém ter pedido. */
  overflow-x: hidden !important;
  /* rolar dentro do dropdown não arrasta a página atrás dele */
  overscroll-behavior: contain;
  /* barra fina no Firefox */
  scrollbar-width: thin;
}

/* Painel teletransportado para o <body> (`append-to-body`, padrão do DropDownConsulte com
   busca). O tema dá z-index 100, que fica ATRÁS do ModalConsulte (1050) e do DialogoSistema
   (3100): o combo abria por baixo do modal e não dava para escolher nada. Acima dos dois. */
body > .multiselect-dropdown {
  z-index: 3200;
}

/* O contêiner que rola é o .multiselect-dropdown; a lista acompanha o conteúdo. */
.multiselect-options {
  max-height: none;
  overflow: visible;
}

/* Barra de rolagem enxuta e visível (Chrome/Edge/Safari). */
.multiselect-dropdown::-webkit-scrollbar {
  width: 8px;
}

.multiselect-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.multiselect-dropdown::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.28);
  border-radius: 8px;
}

.multiselect-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.45);
}

/* Rótulos longos quebram linha em vez de vazar — comum em disciplinas e escolas.
 *
 * `display: block` desfaz o `display: flex` do tema. Nos combos com slot #option que
 * misturam TEXTO e <span class="badge"> ("CIÊNCIAS [Regente da turma — Fulano]"), o texto
 * solto virava um item de flex ANÔNIMO e disputava a largura com o badge; o badge não
 * cedia espaço e o texto era espremido até a largura mínima. Em fluxo normal os dois são
 * conteúdo inline e quebram como texto, que é o que o layout dessas opções espera.
 *
 * `break-word` no lugar de `anywhere` pelo mesmo motivo: só o `anywhere` reduz o
 * min-content a UM caractere, e era ele que permitia o texto colapsar em uma letra por
 * linha ("E / D / U / C / A / Ç / Ã / O"). Os dois quebram palavra que não cabe na linha —
 * a proteção contra vazamento continua igual.
 *
 * Nenhum slot #option do projeto depende de a opção ser contêiner flex: são texto puro ou
 * spans simples. */
.multiselect-option {
  display: block;
  white-space: normal;
  overflow-wrap: break-word;
}

/* Badge DENTRO da opção quebra linha como texto comum.
 *
 * O tema Hyper declara `.badge { white-space: nowrap }` (light.css), pensado para rótulos
 * curtos tipo "Novo" ou "3". Vários combos usam badge para conteúdo longo — a alocação da
 * disciplina traz o nome inteiro do professor ("1ª alocação — ALESSANDRA FONSECA LEITE DE
 * SOUZA") — e o nowrap fazia a opção ficar mais larga que o dropdown: o rótulo aparecia
 * cortado na borda direita, sem indicação de que havia texto além dali.
 *
 * `text-align: left` porque `.badge` centraliza, e centralizar um badge de duas linhas
 * desalinha o texto do resto da opção. */
.multiselect-option .badge,
.multiselect-single-label .badge {
  white-space: normal;
  text-align: left;
}

/* Rótulo do valor SELECIONADO não vaza por cima da seta.
 *
 * O tema já corta com reticências, mas em `.multiselect-single-label-text` — e as telas que
 * substituem o slot #singlelabel montam a própria div e não usam essa classe. */
.multiselect-single-label {
  overflow: hidden;
}

/* Telas de toque: alvo mínimo de 44px (WCAG 2.5.5) e sem zoom automático no iOS,
   que acontece quando o campo de busca tem fonte menor que 16px. */
@media (hover: none) and (pointer: coarse) {
  .multiselect-option {
    --ms-option-py: 0.8125rem;
    min-height: 44px;
  }

  .multiselect-search,
  .multiselect-tags-search {
    font-size: 16px;
  }
}

/* .wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;

} */

/*
 * REMOVIDO: bloco `.car-road` / `.road` / `.car-wrapper` / `@keyframes go` /
 * `@keyframes roter` (cerca de 150 linhas, com as quatro variantes com prefixo de
 * fabricante).
 *
 * Eram CSS ÓRFÃO: nenhum template do projeto usa essas classes (verificado em `src/` e
 * em `public/`). Saíram por dois motivos, os dois de acessibilidade:
 *
 * 1. WCAG 2.2.2 (nível A) — `animation: go 2s infinite` é movimento que dura mais de 5
 *    segundos e não tinha nenhuma forma de pausar, parar ou esconder. Enquanto a regra
 *    existisse, bastava alguém reaproveitar a classe para reintroduzir a falha.
 * 2. Este bloco <style> do App.vue NÃO é scoped, então `.road`, `.title` e afins valiam
 *    para o sistema inteiro — nomes genéricos o bastante para colidir com qualquer tela
 *    nova.
 *
 * O auditor `npm run a11y` passou a ter a regra `animacaoInfinita` justamente para que
 * um caso destes não volte despercebido.
 */

/*********** ConsulteGrid ******************/
.page-link {
  cursor: pointer;
}

/*
 * As regras que existiam aqui empurravam `.asw-menu-btn` e `.asw-menu` (widget de
 * acessibilidade Sienna) para o canto DIREITO, contradizendo o
 * `html body .asw-menu-btn { left: 20px }` do public/index.html — que tem
 * especificidade maior (0,1,2 contra 0,1,0) e portanto vencia. O efeito era o
 * botão à esquerda e o painel à direita, em cantos opostos.
 *
 * A posição agora é definida em um lugar só (public/index.html) e o ajuste de
 * largura do painel no celular está em assets/scss/_acessibilidade.scss.
 */
</style>
